import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import {
  RecruitmentFormLayout,
  RecruitmentFormData,
} from './components/RecruitmentFormLayout'
import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../../services/network'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { RecruitmentStatusValue } from '../../enums/RecruitmentStatus'
import Breadcrumb from '../../components/common/breadcrumb'

/**
 * 共創パートナー募集編集ページ
 */
const RecruitmentEditPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const apiClient = getKyousouApiClient()
  const { showSnackBar } = useSnackBar()

  const [currentStatus, setCurrentStatus] =
    useState<RecruitmentStatusValue>('draft')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RecruitmentFormData>({
    defaultValues: {
      title: '',
      description: '',
      requirements: '',
      legalMatters: '',
      thumbnailUrl: 'https://via.placeholder.com/300x300?text=No+Image',
      status: 'draft',
      scheduledDate: '',
      sdgsCategories: [],
    },
  })

  // 募集データの取得とフォーム初期化
  useEffect(() => {
    const fetchRecruitment = async () => {
      if (!id) {
        showSnackBar(['募集IDが指定されていません'], 'error')
        navigate('/recruitment/manage')
        return
      }

      try {
        const response =
          await apiClient.partnerRecruitments.partnerRecruitmentsDetail(id)

        const recruitmentData = response.data

        if (recruitmentData) {
          // 自分の募集でない場合はアクセス拒否
          if (!recruitmentData.is_own_recruitment) {
            showSnackBar(['この募集を編集する権限がありません'], 'error')
            navigate('/recruitment/manage')
            return
          }

          const {
            title,
            description,
            requirements,
            legal_matters,
            thumbnail_url,
            status,
            sdgs_categories,
            scheduled_date,
          } = recruitmentData

          setCurrentStatus(status as RecruitmentStatusValue)

          // 予約投稿日をフォーム用の形式（YYYY-MM-DD）に変換
          let formattedScheduledDate = ''
          if (scheduled_date) {
            const date = new Date(scheduled_date)
            // YYYY-MM-DD形式に変換
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            formattedScheduledDate = `${year}-${month}-${day}`
          }

          reset({
            title: title || '',
            description: description || '',
            requirements: requirements || '',
            legalMatters: legal_matters || '',
            thumbnailUrl: thumbnail_url || '',
            status: status as RecruitmentStatusValue,
            scheduledDate: formattedScheduledDate,
            sdgsCategories: sdgs_categories || [],
          })
        } else {
          showSnackBar(['指定された募集が見つかりません'], 'error')
          navigate('/recruitment/manage')
        }
      } catch (error) {
        const apiError = error as ApiErrorResponse
        if (apiError.status === 404) {
          showSnackBar(['指定された募集が見つかりません'], 'error')
        } else {
          handleError(apiError, showSnackBar)
        }
        navigate('/recruitment/manage')
      }
    }

    fetchRecruitment()
  }, [id])

  // 公開・更新時の処理
  const onSubmit = async (data: RecruitmentFormData) => {
    if (!id) {
      showSnackBar(['募集IDが不明です'], 'error')
      return
    }

    // 予約投稿日をY-m-d H:i:s形式に変換
    let formattedScheduledDate = undefined
    if (data.status === 'scheduled' && data.scheduledDate) {
      // YYYY-MM-DD形式の日付をYYYY-MM-DD 00:00:00形式に変換
      formattedScheduledDate = `${data.scheduledDate} 00:00:00`
    }

    const response = await apiClient.partnerRecruitments
      .partnerRecruitmentsUpdate(id, {
        title: data.title,
        description: data.description,
        requirements: data.requirements || '',
        legalMatters: data.legalMatters || '',
        thumbnailUrl: data.thumbnailUrl || undefined,
        status: data.status || 'published',
        scheduledDate: formattedScheduledDate || undefined,
        sdgsCategories: data.sdgsCategories,
      })
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })

    if (response) {
      showSnackBar(['募集を更新しました'], 'success')
      navigate('/recruitment/manage')
    }
  }

  // 下書き保存時の処理（statusは必ずdraftになる）
  const onSaveDraft = async (data: RecruitmentFormData) => {
    if (!id) {
      showSnackBar(['募集IDが不明です'], 'error')
      return
    }

    const response = await apiClient.partnerRecruitments
      .partnerRecruitmentsUpdate(id, {
        title: data.title || '',
        description: data.description || '',
        requirements: data.requirements || '',
        legalMatters: data.legalMatters || '',
        thumbnailUrl: data.thumbnailUrl || undefined,
        status: 'draft',
        scheduledDate: data.scheduledDate || undefined,
        sdgsCategories: data.sdgsCategories || [],
      })
      .catch((e: ApiErrorResponse) => {
        handleError(e, showSnackBar)
        return null
      })

    if (response) {
      showSnackBar(['下書きを保存しました'], 'success')
      navigate('/recruitment/manage')
    }
  }

  const handleCancel = () => navigate('/recruitment/manage')

  /**
   * 下書き保存ボタンクリック時の処理
   * バリデーションをスキップして下書き保存を実行
   */
  const handleSaveDraft = () => {
    // フォームの現在の値を取得してバリデーションなしで下書き保存
    const currentValues = watch()
    onSaveDraft(currentValues)
  }

  return (
    <AuthenticatedLayout>
      <Breadcrumb
        items={[
          { label: 'ホーム', path: '/dashboard' },
          { label: '自社の募集', path: '/recruitment/manage' },
          { label: '編集' },
        ]}
      />

      <RecruitmentFormLayout
        title="共創パートナー募集編集"
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        errors={errors}
        isSubmitting={isSubmitting}
        isEditMode={true}
        currentStatus={currentStatus}
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        watch={watch}
        setValue={setValue}
        control={control}
      />
    </AuthenticatedLayout>
  )
}

export default RecruitmentEditPage
